import React, {useEffect, useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import {
  Box,
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import {ReactComponent as ColumnsIcon} from '../../../../../../assets/images/icon-columns.svg';
import {ReactComponent as UploadIcon} from '../../../../../../assets/images/upload.svg';
import {ReactComponent as SaveIcon} from '../../../../../../assets/images/save.svg';
import {ReactComponent as CheckedIcon} from '../../../../../../assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../../../assets/images/icon-unchecked.svg';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import ArrMenu from '../../../../../common/arr-menu/ArrMenu';
import SearchTermReportTotalCount from '../../../reports/search-term/SearchTermReportTotalCount';
import './RightPanelHeader.scss';
import {RightPanelConfig} from '../../../config/reportTypes';
import {ActionMenuPayloadType} from '../../../reports/search-term/SearchTermReportContainer';
import {CustomMenuItem} from '../../../../../common/arr-menu/ArrMenu';

export interface SelectSubItem {
  id: string | number;
  name: string;
  disabled?: boolean;
  checked?: boolean;
}

export interface TableHeaderProps {
  tableColumns?: SelectSubItem[];
  onTableColumnSelectionChange?: (arg0: SelectSubItem) => void;
  onSaveClick?: () => void;
  onUploadClick?: () => void;
  isSavePanelOpen: boolean;
  setIsSavePanelOpen: (isSavePanelOpen: boolean) => void;
  rightPanelHeaderConfig?: RightPanelConfig;
  isNoData?: boolean;
  isSaveReportInActive?: boolean;
  canSave?: boolean;
  isQueryRun?: boolean;
  tiles?: {
    key: string;
    label: string;
    total: number;
    selected: boolean;
    disabled?: boolean;
  }[];
  onTileChange?: (key: string, selected: boolean) => void;
  totalSelectedCount?: number;
  data?: any;
  isExportDataReport?: boolean;
  actionMenuPayload?: ActionMenuPayloadType;
  onActionSelect?: (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => void;
}

const RightPanelHeader: React.FC<TableHeaderProps> = ({
  tableColumns = [],
  onTableColumnSelectionChange,
  onSaveClick,
  onUploadClick,
  isSavePanelOpen,
  setIsSavePanelOpen,
  isNoData,
  rightPanelHeaderConfig,
  isSaveReportInActive = false,
  canSave = false,
  isQueryRun,
  tiles = [],
  onTileChange,
  totalSelectedCount,
  data,
  isExportDataReport = false,
  actionMenuPayload,
  onActionSelect,
}) => {
  const [totalCountData, setTotalCountData] = useState(data);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const columnMenuRef = useRef<any>(null);

  useEffect(() => {
    const masked: any = {...(data || {})};
    hiddenKeys.forEach((k) => {
      if (k in masked) {
        // eslint-disable-next-line security/detect-object-injection
        delete masked[k];
      }
    });
    setTotalCountData(masked);
  }, [data, hiddenKeys]);

  const columnMenuItemClickHandler = (columnMenuItem: any) => {
    onTableColumnSelectionChange?.({
      id: columnMenuItem.id,
      name: columnMenuItem.label,
    });

    setHiddenKeys((prev) => {
      const next = new Set(prev);
      switch (columnMenuItem.id) {
        case 'pureHits':
          if (columnMenuItem.checked) {
            next.add('pureHits');
          } else {
            next.delete('pureHits');
          }
          break;
        case 'emailThreads':
          if (columnMenuItem.checked) {
            next.add('emailThreads');
          } else {
            next.delete('emailThreads');
          }
          break;
        case 'families':
          if (columnMenuItem.checked) {
            next.add('families');
          } else {
            next.delete('families');
          }
          break;
        case 'nearDupes':
          if (columnMenuItem.checked) {
            next.add('nearDupes');
          } else {
            next.delete('nearDupes');
          }
          break;
        case 'uniqueHits':
          if (columnMenuItem.checked) {
            next.add('uniqueHits');
          } else {
            next.delete('uniqueHits');
          }
          break;
        default:
          break;
      }
      return next;
    });

    setTotalCountData(() => {
      const masked: any = {...(data || {})};
      const hk = new Set(
        Array.from(hiddenKeys).concat(
          columnMenuItem.id === 'hits' && columnMenuItem.checked
            ? ['pureHits']
            : columnMenuItem.id === 'hits' && !columnMenuItem.checked
              ? []
              : columnMenuItem.id === 'emailThreadedDocs' &&
                  columnMenuItem.checked
                ? ['emailThreads']
                : columnMenuItem.id === 'emailThreadedDocs' &&
                    !columnMenuItem.checked
                  ? []
                  : columnMenuItem.id === 'familyMembers' &&
                      columnMenuItem.checked
                    ? ['families']
                    : columnMenuItem.id === 'familyMembers' &&
                        !columnMenuItem.checked
                      ? []
                      : columnMenuItem.id === 'nearDupes' &&
                          columnMenuItem.checked
                        ? ['nearDupes']
                        : columnMenuItem.id === 'nearDupes' &&
                            !columnMenuItem.checked
                          ? []
                          : columnMenuItem.id === 'uniqueHits' &&
                              columnMenuItem.checked
                            ? ['uniqueHits']
                            : columnMenuItem.id === 'uniqueHits' &&
                                !columnMenuItem.checked
                              ? []
                              : []
        )
      );
      hk.forEach((k) => {
        if (k in masked) {
          // eslint-disable-next-line security/detect-object-injection
          delete masked[k];
        }
      });
      return masked;
    });
  };

  const convertToCustomMenuItems = (items: SelectSubItem[]) =>
    items.map((item) => ({
      id: item.id.toString(),
      label: item.name,
      disabled: item.disabled,
      checked: item.checked,
    }));

  const showHeaderContent = useMemo(() => {
    if (_.isNil(isQueryRun)) {
      return !isNoData;
    }
    return isQueryRun;
  }, [isQueryRun, isNoData]);

  const saveDisabled = !!(isSaveReportInActive || !canSave);

  if (isExportDataReport) return null;
  return (
    <>
      <Box className="table-header">
        <Box className="table-header-section">
          {showHeaderContent && (
            <Box className="header-title-section">
              {rightPanelHeaderConfig?.title && (
                <Typography
                  className="typo-text heading-text subhead3_size"
                  tabIndex={0}
                  data-testid={`right-panel-header-title`}
                >
                  {rightPanelHeaderConfig?.title}
                </Typography>
              )}
              {rightPanelHeaderConfig?.subTitle && (
                <Typography
                  className="typo-text time-text body4_size text-body-subtle"
                  tabIndex={0}
                  data-testid={`right-panel-header-typography-subtitle`}
                >
                  {rightPanelHeaderConfig?.subTitle}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <Box className="table-header-actions">
          <Box
            className="action-buttons-right"
            role="group"
            aria-label="Action buttons"
          >
            {rightPanelHeaderConfig?.showUploadIcon && (
              <ArrIconButton
                size="large"
                onClick={() => {
                  onUploadClick?.();
                }}
                ariaLabel="Export"
                icon={
                  <UploadIcon
                    className={`${isNoData ? 'inactive-icon' : ''}`}
                  />
                }
                shape={isNoData ? 'none' : 'default'}
                disabled={isNoData}
                className="header-icon-button"
                testId="report-export-button"
              />
            )}

            {rightPanelHeaderConfig?.showSaveIcon && (
              <ArrIconButton
                size="large"
                onClick={() => {
                  if (saveDisabled) return;
                  onSaveClick?.();
                  setIsSavePanelOpen(!isSavePanelOpen);
                }}
                ariaLabel="Save"
                icon={
                  <SaveIcon
                    fontSize="medium"
                    className={`${saveDisabled ? 'inactive-icon' : ''} `}
                  />
                }
                shape={saveDisabled ? 'none' : 'default'}
                disabled={saveDisabled}
                testId="report-save-button"
              />
            )}

            {rightPanelHeaderConfig?.showColumnsIcon && (
              <ArrIconButton
                size="large"
                onClick={(e) => columnMenuRef.current?.open(e.currentTarget)}
                ariaLabel="Columns"
                icon={
                  <ColumnsIcon
                    fontSize="medium"
                    className={`${isNoData ? 'inactive-icon' : ''}`}
                  />
                }
                shape={isNoData ? 'none' : 'default'}
                disabled={isNoData}
                testId="report-columns-button"
              />
            )}
          </Box>
        </Box>
      </Box>
      {rightPanelHeaderConfig?.title === 'Total Results (All Pages)' &&
        !isNoData && (
          // tiles?.some((tile) => tile.selected) &&
          <Box className="search-term-report-section" mt={2}>
            <SearchTermReportTotalCount
              tiles={tiles}
              onTileChange={onTileChange}
              data-testid="search-term-report-total-count"
              data={totalCountData}
              totalSelectedCount={totalSelectedCount}
              actionMenuPayload={actionMenuPayload}
              onActionSelect={onActionSelect}
            />
          </Box>
        )}
      <ArrMenu
        ref={columnMenuRef}
        items={convertToCustomMenuItems(tableColumns)}
        direction="bottom-left"
        className="doc-column-menu"
        id="right-panel-menu"
        keepOpenOnItemClick={true}
        renderItem={(item) => (
          <MenuItem
            dense
            disableRipple
            disabled={item.disabled}
            onClick={() => columnMenuItemClickHandler(item)}
            role="menuitemcheckbox"
            aria-checked={item.checked}
            data-testid={`right-panel-header-menu-item-${item.id}`}
          >
            <ListItemIcon>
              <Checkbox
                data-testid={`standard-report-right-panel-column-checkbox-${item.id}`}
                edge="start"
                checked={item.checked}
                tabIndex={-1}
                disableRipple
                size="small"
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                disabled={item.disabled}
              />
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        )}
      />
    </>
  );
};

export default RightPanelHeader;
